import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { FeatureToggle, FeatureToggleMap } from './feature-toggle.model';

/**
 * TVG-100: client-side gate for global feature toggles.
 *
 * loadActive() must be called once after login; the cached map then powers
 * `isEnabled()` for synchronous use (sidebar filtering, template @if blocks)
 * and `state$` for reactive use (route guard).
 *
 * In production an unknown key is treated as disabled. In dev the backend
 * reports every registered key as enabled, so this service inherits that
 * behaviour automatically.
 */
@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  private readonly baseUrl = `${environment.apiUrl}/api/feature-toggles`;
  private readonly state = new BehaviorSubject<FeatureToggleMap>({});
  private loaded = false;
  private inFlight?: Observable<FeatureToggleMap>;

  readonly state$ = this.state.asObservable();

  constructor(private http: HttpClient) {}

  loadActive(force = false): Observable<FeatureToggleMap> {
    if (this.loaded && !force) {
      return of(this.state.value);
    }
    if (this.inFlight && !force) {
      return this.inFlight;
    }

    this.inFlight = this.http
      .get<{ data: FeatureToggleMap }>(`${this.baseUrl}/active`)
      .pipe(
        map((res) => res.data ?? {}),
        tap((data) => {
          this.state.next(data);
          this.loaded = true;
          this.inFlight = undefined;
        }),
      );

    return this.inFlight;
  }

  isEnabled(key: string): boolean {
    return this.state.value[key] === true;
  }

  isEnabled$(key: string): Observable<boolean> {
    return this.state$.pipe(map((map) => map[key] === true));
  }

  clearCache(): void {
    this.loaded = false;
    this.state.next({});
  }

  list(): Observable<FeatureToggle[]> {
    return this.http
      .get<{ data: FeatureToggle[] }>(this.baseUrl)
      .pipe(map((res) => res.data ?? []));
  }

  update(key: string, enabled: boolean): Observable<FeatureToggle> {
    return this.http
      .put<{ data: FeatureToggle }>(`${this.baseUrl}/${encodeURIComponent(key)}`, { enabled })
      .pipe(
        map((res) => res.data),
        tap((toggle) => {
          this.state.next({ ...this.state.value, [toggle.key]: toggle.enabled });
        }),
      );
  }
}
